import React, { useEffect, useState } from 'react';
import { Grid, Stack, Typography } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { CardList, emptyFieldHolder } from 'components/molecules/CardList';
import { CaseStatus, ICaseDecision } from 'models/cases';
import { formatDate } from 'utils/date';
import {
  StyledDetail,
  StyledTableCard,
  StyledTableCardItem,
} from 'pages/CaseManagementPage/CasesPage/styles';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { Table } from 'components/molecules/Table';
import { useCase } from '../../hooks';
import { useParams } from 'react-router-dom';
import { FormModal } from 'components/molecules/Modal';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Elabels, summaryReportTableColumns } from '../../helpers';
import { EIconTypes } from 'constants/Icons';
import { downloadUrlAsFile } from 'utils/files';
import { useAccount } from 'pages/AccountPage/hooks';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';

interface ISummaryReport {
  caseDecision: ICaseDecision;
  statusUniqueId: string;
  summaryReportGenerated: boolean;
  summaryReportName: string | null;
  legalEntityId: string;
  updateCase: () => void;
}

const renderDecisionChipVariant = (statusUniqueId: string) => {
  switch (statusUniqueId as CaseStatus) {
    case CaseStatus.GRANTED:
    case CaseStatus.CLOSED:
      return EChipsVariants.success;
    case CaseStatus.REFUSED:
    case CaseStatus.REJECTED:
      return EChipsVariants.error;
    default:
      return EChipsVariants.primary;
  }
};

const SummaryReport = ({
  caseDecision,
  statusUniqueId,
  summaryReportGenerated,
  summaryReportName,
  legalEntityId,
  updateCase,
}: ISummaryReport) => {
  const { t } = useTranslations();
  const { isDesktop } = useThemeBreakpoint();
  const { caseId = '' } = useParams<{ caseId: string }>();
  const { longDateFormat } = useGlobalProperty();
  const [isLoading, setIsLoading] = useState(false);
  const [generateModal, setGenerateModal] = useState(false);
  const { handleGenerateSummaryReport, getSummaryReport } = useCase();
  const { account, getSelfAccountData } = useAccount();
  const { getLegalEntityLicenceAuthoritiesData, legalEntityLicenceAuthorities } =
    useLegalEntities();
  const [canGenerateReport, setCanGenerateReport] = useState(false);

  useEffect(() => {
    if (!summaryReportGenerated) {
      getSelfAccountData();
    }
  }, [summaryReportGenerated]);

  useEffect(() => {
    (async () => {
      if (legalEntityId && !summaryReportGenerated) {
        await getLegalEntityLicenceAuthoritiesData(legalEntityId);
      }
    })();
  }, [legalEntityId, summaryReportGenerated]);

  useEffect(() => {
    if (legalEntityLicenceAuthorities) {
      const statusAllowsReportGeneration = [
        CaseStatus.GRANTED,
        CaseStatus.CLOSED,
        CaseStatus.REJECTED,
        CaseStatus.REFUSED,
      ].includes(statusUniqueId as CaseStatus);

      const userHasMatchingLicenceAuthority = !!(
        account?.licenceAuthorityIds.includes(
          legalEntityLicenceAuthorities.primaryLicenceAuthorityUniqueId,
        ) ||
        account?.licenceAuthorityIds.includes(
          legalEntityLicenceAuthorities.secondaryLicenceAuthorityUniqueId,
        )
      );

      setCanGenerateReport(statusAllowsReportGeneration && userHasMatchingLicenceAuthority);
    }
  }, [legalEntityLicenceAuthorities]);

  const onClickGenerate = async () => {
    setIsLoading(true);
    const result = await handleGenerateSummaryReport(caseId);
    if (result) {
      updateCase();
    }
    setIsLoading(false);
    setGenerateModal(false);
  };

  const handleView = async () => {
    if (summaryReportGenerated) {
      const url = await getSummaryReport(caseId);
      if (url) {
        window.open(url, '_blank');
      }
    }
  };
  const handleDownload = async () => {
    if (summaryReportGenerated) {
      const url = await getSummaryReport(caseId, true);
      if (url) {
        downloadUrlAsFile(url, summaryReportName ?? '');
      }
    }
  };

  return (
    <Grid sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <FormModal
        open={generateModal}
        onClickClose={() => setGenerateModal(false)}
        onClickApprove={onClickGenerate}
        approveButtonLabel="general.yes.label"
        cancelButtonLabel="general.no.label"
        title={t('caseManagement.cases.generateReport.label')}
        titleLeft
        text={t('caseManagement.cases.areYouSureGenerateSummaryReport.text')}
      />
      <Grid
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography variant="body2">{t('caseManagement.cases.summaryReport.text')}</Typography>
        {!summaryReportGenerated && canGenerateReport && (
          <Button
            id="generate-report"
            label={t('caseManagement.cases.generateReport.label')}
            variant={EButtonVariants.contained}
            onClick={() => setGenerateModal(true)}
            disabled={isLoading}
            isLoading={isLoading}
          />
        )}
      </Grid>
      <StyledDetail container size={{ xs: 12 }} columnSpacing={1}>
        <Grid size={{ xs: 12, lg: 3 }}>
          <Stack>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('caseManagement.cases.decision.label')}
            </Typography>
            <Typography variant="body2">
              <Chips
                text={emptyFieldHolder(caseDecision.decision)}
                variant={renderDecisionChipVariant(statusUniqueId)}
              />
            </Typography>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, lg: 3 }}>
          <Stack>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('caseManagement.cases.decisionReason.label')}
            </Typography>
            <Typography variant="body2">
              {emptyFieldHolder(caseDecision.reasons?.join(', '))}
            </Typography>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, lg: 3 }}>
          <Stack>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('caseManagement.cases.dateOfDecision.label')}
            </Typography>
            <Typography variant="body2">
              {caseDecision.createdDate
                ? formatDate(caseDecision.createdDate, longDateFormat)
                : emptyFieldHolder(caseDecision.createdDate)}
            </Typography>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, lg: 3 }}>
          <Stack>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('caseManagement.cases.authorOfDecision.label')}
            </Typography>
            <Typography variant="body2">
              {caseDecision.authorName
                ? `${caseDecision.authorName} (${caseDecision.authorDisplayUniqueId})`
                : emptyFieldHolder(caseDecision.authorName)}
            </Typography>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Stack>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('caseManagement.cases.decisionComment.label')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
            >
              {emptyFieldHolder(caseDecision.comment)}
            </Typography>
          </Stack>
        </Grid>
      </StyledDetail>
      {summaryReportGenerated && (
        <>
          {isDesktop ? (
            <Table
              columns={summaryReportTableColumns({
                handleView,
                handleDownload,
              })}
              dataSource={[{ id: 1, name: summaryReportName }]}
              loader={isLoading}
            />
          ) : (
            <CardList
              items={[{ id: 1, name: summaryReportName }]}
              render={(data, index) => (
                <StyledTableCard container>
                  <StyledTableCardItem size={{ xs: 12 }}>
                    <Typography variant="subtitle2">
                      {t(Elabels.verificationSummaryReport)}
                    </Typography>
                    <Typography variant="body2">{data.name}</Typography>
                  </StyledTableCardItem>
                  <StyledTableCardItem container spacing={2}>
                    <Grid size={{ xs: 12, sm: 3 }} sx={{ mb: { xs: 2, sm: 0 } }}>
                      <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
                        {`${t('general.actions.button')}:`}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <Button
                        id={`case_summary_report_view-button-${data.id}`}
                        variant={EButtonVariants.outlined}
                        tableButton
                        icon={EIconTypes.view}
                        onClick={handleView}
                      />
                      <Button
                        id={`case_summary_report_download-button-${data.id}`}
                        variant={EButtonVariants.outlined}
                        tableButton
                        icon={EIconTypes.download}
                        onClick={handleDownload}
                      />
                    </Grid>
                  </StyledTableCardItem>
                </StyledTableCard>
              )}
            />
          )}
        </>
      )}
    </Grid>
  );
};

export default SummaryReport;
